import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { apiBaseURL } from '../request'

export default function Home() {
  const { oid } = useParams()
  const [merchant, setMerchant] = useState('')
  const [conversationId] = useState(uuidv4())
  const [faq, setFaq] = useState([])
  const [category, setCategory] = useState([])
  const [chats, setChats] = useState([])
  const chatsEndRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const textareaRef = useRef(null)
  const [isRecording, setIsRecording] = useState(false)
  const recognitionRef = useRef(null)
  const [isWeixin, setIsWeixin] = useState(false)
  const recordTimerRef = useRef(null)

	useEffect(() => {
		if (merchant?.ai_name) {
			document.title = merchant.ai_name
		}
  }, [merchant])

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase()
    setIsWeixin(/micromessenger/.test(ua))
  }, [])

  useEffect(() => {
    if (chatsEndRef.current) {
      chatsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chats])

  const autoResize = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = textarea.scrollHeight + "px"
    }
  }

  useEffect(() => {
    if (isWeixin) return
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      console.warn('浏览器不支持语音识别')
      return
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.lang = 'zh-CN'
    recognition.interimResults = true
    recognition.continuous = true
    recognition.onresult = (event) => {
      let transcript = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript
      }
      setInputValue(transcript)
      autoResize()
    }
    recognition.onerror = (event) => {
      console.error('语音识别错误:', event.error)
      setIsRecording(false)
    }
    recognitionRef.current = recognition
  }, [isWeixin])

  useEffect(() => {
    if (!isWeixin) return
    async function initWeixinConfig() {
      try {
        const res = await axios.post('/c/chatbot/weixin/config', {
          url: window.location.href.split('#')[0]
        })
        const { appId, timestamp, nonceStr, signature } = res.data
        window.wx.config({
          debug: false,
          appId,
          timestamp,
          nonceStr,
          signature,
          jsApiList: ['startRecord', 'stopRecord', 'translateVoice']
        })
      } catch (err) {
        console.error('获取微信 config 参数失败:', err)
      }
    }
    initWeixinConfig()
  }, [isWeixin])

  const startRecording = () => {
    if (isWeixin) {
      window.wx.startRecord({
        success: () => {
          setIsRecording(true)
          recordTimerRef.current = setTimeout(() => {stopRecording()}, 8000)
        }
      })
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.start()
        setIsRecording(true)
        recordTimerRef.current = setTimeout(() => {stopRecording()}, 8000)
      }
    }
  }

  const stopRecording = () => {
    if (recordTimerRef.current) {
      clearTimeout(recordTimerRef.current)
      recordTimerRef.current = null
    }

    if (isWeixin) {
      window.wx.stopRecord({
        success: function (res) {
          const localId = res.localId
          window.wx.translateVoice({
            localId,
            isShowProgressTips: 1,
            success: function (res) {
              if (res.translateResult) {
                setInputValue(res.translateResult)
                autoResize()
              }
              setIsRecording(false)
            },
            fail: function (err) {
              console.error('语音翻译失败:', err)
              setIsRecording(false)
            }
          })
        }
      })
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        setIsRecording(false)
      }
    }
  }

  const fetchMerchant = () => {
    axios.post('/c/chatbot/merchant/get', {
      merchant_oid: oid
    })
      .then(function (response) {
        if (response.errcode === 0) {
          setMerchant(response.data)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }

  const fetchFaqs = () => {
    axios.post('/c/chatbot/faqs/get', {
      merchant_oid: oid,
      per_page: 5
    })
      .then(function (response) {
        if (response.errcode === 0) {
          setFaq(response.data.faqs)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }

  const fetchCategories = () => {
    axios.post('/c/chatbot/categories/get', {
      merchant_oid: oid,
      per_page: 10
    })
      .then(function (response) {
        if (response.errcode === 0) {
          setCategory(response.data.categories)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }

  useEffect(() => {
    fetchFaqs()
    fetchCategories()
    fetchMerchant()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid])

  useEffect(() => {
    const handleClickOutside = (event) => {
      const bottomEl = document.querySelector('.home-bottom')
      if (bottomEl && !bottomEl.contains(event.target)) {
        if (textareaRef.current) {
          textareaRef.current.blur()
        }
      }
    }
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [])

  const handleSendMessage = (message) => {
    if (!message.trim()) return
    const userMessage = { id: Date.now(), type: 'user', content: message }
    setChats(prev => [...prev, userMessage])
    setInputValue("")
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px"
    }

    setLoading(true)
    let accumulatedText = ''
    let messageSource = 'knowledge'
    const kefuMessageId = Date.now() + 1
    const es = new EventSource(`${apiBaseURL}/c/chatbot/deepseek/stream?user_question=${encodeURIComponent(message)}&merchant_oid=${oid}&conversation_id=${conversationId}`)

    es.onmessage = (event) => {
      setLoading(false)
      const data = JSON.parse(event.data)
      if (data.type === 'partial') {
        accumulatedText += data.content
        setChats(prev => {
          const exists = prev.find(chat => chat.id === kefuMessageId)
          if (exists) {
            return prev.map(chat =>
              chat.id === kefuMessageId ? { ...chat, content: accumulatedText } : chat
            )
          } else {
            return [...prev, { id: kefuMessageId, type: 'kefu', content: accumulatedText }]
          }
        })
      } else if (data.type === 'final') {
        if (data.source) {
          messageSource = data.source
          setChats(prev => 
            prev.map(chat => 
              chat.id === kefuMessageId ? { ...chat, source: messageSource } : chat
            )
          )
        }
        es.close()
        setLoading(false)
      }
    }
    es.onerror = () => {
      es.close()
      setLoading(false)
    }
  }

  const handleTagClick = async (tag, oid) => {
    try {
      const res = await axios.post('/c/chatbot/category/questions/get', {
        category_oid: oid,
        per_page: 6
      })

      if (res.data && res.data.questions) {
        const cardMessage = {
          id: Date.now(),
          type: 'card',
          content: {
            title: `与${tag}相关的问题`,
            options: res.data.questions
          }
        }
        setChats(prev => [...prev, cardMessage])
      }
    } catch (error) {
      console.error('获取问题列表失败:', error)
    }
  }

  return (
    <div className='home-page'>
      <div className='main-content'>
        <div className='home-welcome'>
            <div className='home-welcome-kefu'>
                <img src={merchant.logo} alt="" />
                <p>{merchant.ai_name}</p>
            </div>
            <div className='home-welcome-title'>您好！我是{merchant.name}的智能咨询助手，很高兴为您服务！</div>
        </div>
    
        <div className='home-faqs'>
            <div className='home-faqs-left'>
            <div className='home-faqs-left-up'>猜你<br/>想问</div>
            <div className='home-faqs-left-down' onClick={() => fetchFaqs()}>
                <img src="/images/huanyipi.png" alt="" />
                <span>换一批</span>
            </div>
            </div>
            <div className='home-faqs-right'>
            {faq && faq.map((item, index) => (
                <div className='home-faq' key={index} onClick={() => handleSendMessage(item)}>{item}</div>
            ))}
            </div>
        </div>

        <div className='home-chats'>
            {chats.map(chat => (
            <div key={chat.id} className={`chat-item ${chat.type}-chat`}>
                {chat.type === 'card' ? (
                <div className="chat-card">
                    <div className="card-title">{chat.content.title}</div>
                    <div className="card-options">
                    {chat.content.options.map((q, i) => (
                        <div
                        key={i}
                        className="card-option"
                        onClick={() => handleSendMessage(q)}
                        >
                        {q}
                        </div>
                    ))}
                    </div>
                </div>
                ) : (
                <div className="chat-bubble">
                    <div className="chat-bubble-content">{chat.content}</div>
                    {chat.type === 'kefu' && chat.source === 'deepseek' && (
                    <div className="chat-bubble-tip">本回答由 AI 生成，内容仅供参考，请仔细甄别。</div>
                    )}
                </div>
                )}
            </div>
            ))}

            {loading && (
            <div className="chat-loading">
                <img src="/images/loading.gif" alt="思考中..." />
            </div>
            )}
            <div ref={chatsEndRef} />
        </div>
      </div>

      <div className='home-bottom'>
        {category.length > 2 && (
          <div className='home-bottom-tags'>
            {category.map((item, index) => (
              <div 
                className='home-bottom-tag' 
                key={index}
                onClick={() => handleTagClick(item.name, item.oid)}
              >
                {item.name}
              </div>
            ))}
          </div>
        )}
        
        {isRecording ? (
          <div className="home-bottom-voice">
            <button className="voice-finish" onClick={stopRecording}>完成</button>
            <div className="voice-wave">
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item bar" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
              <span className="wave-item dot" />
            </div>
          </div>
        ) : (
          <div className='home-bottom-send'>
            <img src="/images/yuyin.png" alt="" onClick={startRecording} />
            <textarea
              ref={textareaRef}
              className='home-input'
              enterKeyHint="send" 
              placeholder='请输入问题，来问我吧...'
              value={inputValue}
              rows={1}
              onChange={(e) => {
                setInputValue(e.target.value)
                autoResize()}}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage(inputValue)
                }
              }}
            />
            <img src="/images/fasong.png" alt="" onClick={() => handleSendMessage(inputValue)} />
          </div>
        )}
      </div>
    </div>
  )
}
